import React from 'react';

import { FormattedMessage } from '../../../util/reactIntl';
import {
  buildEcolabelSearchParams,
  getEcolabelSearchParamName,
  parseEcolabelSearchParam,
} from '../../../config/ecolabels';
import { useValuePriorities } from '../../../hooks/useValuePriorities';

import { ValuePrioritiesPanel } from '../../../components';

import valuePrioritiesCss from '../../../components/ValuePriorities/ValuePriorities.module.css';

const isSameSet = (a, b) => a.length === b.length && a.every(key => b.includes(key));

/**
 * "Your priorities" on the browse page.
 *
 * Shows the shopper's priorities and, once they've set some, a shortcut that
 * filters the results to products carrying at least one of them — the same
 * ecolabel filter as the sidebar, so the two always agree.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className]
 * @param {Array<Object>} props.listingFields config.listing.listingFields
 * @param {Object} props.urlQueryParams the current search params
 * @param {Function} props.onUpdateParams called with the search params to change
 * @returns {JSX.Element}
 */
const ValuePrioritiesSearchBar = props => {
  const { className, listingFields, urlQueryParams, onUpdateParams } = props;
  const priorities = useValuePriorities();

  const prioritiesSearchParams = buildEcolabelSearchParams(priorities, listingFields);
  const activeEcolabelSearch = parseEcolabelSearchParam(urlQueryParams, listingFields);
  const isShowingPriorities =
    !!activeEcolabelSearch &&
    activeEcolabelSearch.mode === 'has_any' &&
    isSameSet(activeEcolabelSearch.keys, priorities);

  const actions = !prioritiesSearchParams ? null : isShowingPriorities ? (
    <>
      <span className={valuePrioritiesCss.panelHint}>
        <FormattedMessage id="ValuePriorities.search.active" />
      </span>
      <button
        type="button"
        className={valuePrioritiesCss.actionButton}
        onClick={() => onUpdateParams({ [getEcolabelSearchParamName(listingFields)]: null })}
      >
        <FormattedMessage id="ValuePriorities.search.showAll" />
      </button>
    </>
  ) : (
    <button
      type="button"
      className={valuePrioritiesCss.actionButtonPrimary}
      onClick={() => onUpdateParams(prioritiesSearchParams)}
    >
      <FormattedMessage id="ValuePriorities.search.apply" />
    </button>
  );

  return <ValuePrioritiesPanel className={className} actions={actions} />;
};

export default ValuePrioritiesSearchBar;
